import React, { useMemo, useState } from 'react'
import type { Chain } from 'viem'
import { WagmiProvider } from 'wagmi'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RainbowKitProvider, darkTheme, lightTheme } from '@rainbow-me/rainbowkit'
import { useTheme } from 'next-themes'
import { getPortalWagmiConfig } from '@/config/wagmi'
import { cotiTestnet, sepolia, cotiMainnet, avalancheFuji, avalanche, ethereumMainnet } from '@/config/chains'
import { hasPersistedWagmiSession } from '@/lib/wagmiSession'
import {
  InitialChainContext,
  type InitialChainContextValue,
} from '@/providers/initialChainContext'
import '@rainbow-me/rainbowkit/styles.css'

const queryClient = new QueryClient()

const RAINBOW_ACCENT = '#1E29F6'

// Lookup of every chain RainbowKit knows about, keyed by numeric chain id.
// Still derived from portal's chain exports (which are built from the plugin's
// getChainConfig), so there is a single source of truth for chain data.
const CHAINS_BY_ID: Record<number, Chain> = {
  [cotiTestnet.id]: cotiTestnet,
  [sepolia.id]: sepolia,
  [cotiMainnet.id]: cotiMainnet,
  [avalancheFuji.id]: avalancheFuji,
  [avalanche.id]: avalanche,
  [ethereumMainnet.id]: ethereumMainnet,
}

interface Props {
  children: React.ReactNode
}

interface RainbowKitThemedProps {
  children: React.ReactNode
  initialChain: Chain
}

/**
 * Inner component that reads the current theme and applies the accent colour.
 * Separated so useTheme() doesn't re-render the whole provider tree on every
 * theme change — only the RainbowKitProvider subtree re-renders.
 */
const RainbowKitThemed: React.FC<RainbowKitThemedProps> = ({ children, initialChain }) => {
  const { resolvedTheme } = useTheme()

  const theme = useMemo(
    () =>
      resolvedTheme === 'light'
        ? lightTheme({ accentColor: RAINBOW_ACCENT })
        : darkTheme({ accentColor: RAINBOW_ACCENT }),
    [resolvedTheme],
  )

  return (
    <RainbowKitProvider
      initialChain={initialChain}
      theme={theme}
      modalSize="compact"
      appInfo={{ appName: 'COTI Privacy Bridge' }}
    >
      {children}
    </RainbowKitProvider>
  )
}

/**
 * Wraps the app with wagmi + RainbowKit + react-query.
 *
 * Uses the plugin's getWagmiConfig() for all connector and chain wiring
 * (EIP-6963 MetaMask, direct Trust Wallet, WalletConnect, all four chains,
 * Sepolia RPC from configureCotiPlugin), then renders its own RainbowKitProvider
 * on top so the portal's dark/light accent theme is preserved.
 *
 * InitialChainContext lets the chain-card selection on WelcomeScreen drive which
 * chain RainbowKit targets at connect time — we manage the state here and pass
 * the current selection to RainbowKitProvider as initialChain.
 *
 * Provider order (required by RainbowKit):
 *   WagmiProvider → QueryClientProvider → RainbowKitProvider
 */
export const WagmiRainbowKitProvider: React.FC<Props> = ({ children }) => {
  const config = useMemo(() => getPortalWagmiConfig(), [])
  // Skip reconnectOnMount when wagmi.store is gone (cleared localStorage).
  // Otherwise reconnect() walks every connector — including WalletConnect —
  // and can stay in `connecting` forever while Index shows a spinner.
  const [reconnectOnMount] = useState(hasPersistedWagmiSession)

  // Default to COTI Testnet; updated per chain-card selection before the
  // RainbowKit connect modal opens.
  const [initialChainId, setInitialChainId] = useState<number>(cotiTestnet.id)
  const initialChain = CHAINS_BY_ID[initialChainId] ?? cotiTestnet

  const contextValue = useMemo<InitialChainContextValue>(
    () => ({ setInitialChainId }),
    [],
  )

  return (
    <WagmiProvider config={config} reconnectOnMount={reconnectOnMount}>
      <QueryClientProvider client={queryClient}>
        <InitialChainContext.Provider value={contextValue}>
          <RainbowKitThemed initialChain={initialChain}>
            {children}
          </RainbowKitThemed>
        </InitialChainContext.Provider>
      </QueryClientProvider>
    </WagmiProvider>
  )
}
